import { Box, Flex, Link, Text, Tooltip } from "@chakra-ui/react";
import React, { useState } from "react";
import { RiIndeterminateCircleLine, RiSearchEyeLine } from "react-icons/ri";
import { ConfirmationDialog } from "src/components/Dialogs/ConfirmationDialog";
import { DocumentViewer } from "src/components/DocumentViewer";
import {
  Document,
  DocumentViewerModal,
} from "src/components/DocumentViewer/V2";
import { MB_SIZE } from "src/constants";

// This component is essentially a list of QuestionFileItem components,
// but QuestionFileItem is tied up with document storage.
export const AttachmentsList: React.FC<{
  attachments: File[];
  onDelete?: (idx: number) => void;
  viewMode?: boolean;
}> = ({ attachments, onDelete, viewMode = false }) => {
  const [previewFile, setPreviewFile] = useState<File | undefined>(undefined);
  const [previewDocument, setPreviewDocument] = useState<Document | undefined>(
    undefined
  );
  const handleClickOnFilePreview = (file: File) => {
    setPreviewFile(file);
    setPreviewDocument({
      type: "url",
      url: URL.createObjectURL(file),
      fileName: file.name,
    });
  };

  // Clean up object URL when component unmounts or previewFile changes
  React.useEffect(() => {
    return () => {
      if (previewDocument && previewDocument.type === "url") {
        URL.revokeObjectURL(previewDocument.url);
      }
    };
  }, [previewDocument]);

  const [fileIdxToDelete, setFileIdxToDelete] = useState<number | undefined>(
    undefined
  );
  const toggleConfirmationDialog = (idx: number) => {
    setFileIdxToDelete(idx);
  };
  const handleDeleteFile = async () => {
    if (fileIdxToDelete === undefined || !onDelete) return;
    onDelete(fileIdxToDelete);
    setFileIdxToDelete(undefined);
  };

  const isV2Supported = DocumentViewer.useIsV2Supported();
  return (
    <Flex direction="column">
      {attachments.length === 0 && (
        <Text color="gray.500" fontSize="sm">
          No attachments
        </Text>
      )}
      {attachments.map((file: File, idx: number) => (
        <Flex direction="column" key={idx}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Link
              onClick={() => handleClickOnFilePreview(file)}
              textDecoration="none"
              textColor="primary.500"
              display="flex"
              alignItems="center"
            >
              <Text display="flex" gap={1} alignItems="inherit">
                {file.name} <RiSearchEyeLine />
              </Text>
            </Link>
            {!viewMode && onDelete && (
              <Tooltip label="Delete file" hasArrow>
                <div>
                  <RiIndeterminateCircleLine
                    cursor="pointer"
                    onClick={() => toggleConfirmationDialog(idx)}
                    color="red"
                  />
                </div>
              </Tooltip>
            )}
          </Box>
          <Text color="gray.500" fontSize="xs">
            {(file.size / MB_SIZE).toFixed(1)}MB
          </Text>
        </Flex>
      ))}

      {isV2Supported ? (
        previewDocument?.type === "url" && (
          <DocumentViewerModal
            isOpen={!!previewDocument}
            onClose={() => {
              URL.revokeObjectURL(previewDocument.url);
              setPreviewDocument(undefined);
            }}
            document={previewDocument}
          />
        )
      ) : (
        <DocumentViewer.V1
          isOpen={!!previewFile}
          file={previewFile}
          title={previewFile?.name ?? ""}
          onClose={() => setPreviewFile(undefined)}
        />
      )}

      <ConfirmationDialog
        isOpen={fileIdxToDelete !== undefined}
        header="Delete file?"
        body="This action can't be undone."
        onCancel={() => setFileIdxToDelete(undefined)}
        onConfirm={handleDeleteFile}
        cancelButton={{ label: "No, cancel" }}
        confirmButton={{ label: "Yes, delete", colorScheme: "red" }}
      />
    </Flex>
  );
};
